import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";
import PopupModal from "../Ui_elements/PopupModal";
import TextFieldComponent from "../TextFieldComponent";
import TimePickerComponent from "../TimePickerComponent";
import { formatDisplayDate, formatDisplayTime } from "./dateFormat";
import { getTimeRangeError } from "./availabilityValidation";
import { STATUS_OPTIONS, STATUSES_WITH_HOURS, getHoursFieldLabels } from "./statusConfig";

const CellEditorPopover = ({
  open,
  onClose,
  cell,
  linguistName,
  date,
  onSave,
  onClear,
  saving,
}) => {
  const [status, setStatus] = useState("");
  const [timeFrom, setTimeFrom] = useState("");
  const [timeTo, setTimeTo] = useState("");
  const [notes, setNotes] = useState("");
  const [timeToError, setTimeToError] = useState("");

  useEffect(() => {
    if (open) {
      setStatus(cell?.eStatus || "");
      setTimeFrom(formatDisplayTime(cell?.time_from));
      setTimeTo(formatDisplayTime(cell?.time_to));
      setNotes(cell?.vNotes || "");
      setTimeToError("");
    }
  }, [open, cell]);

  const showHours = STATUSES_WITH_HOURS.includes(status);
  const hourLabels = getHoursFieldLabels(status);

  const handleTimeFromChange = e => {
    const nextFrom = e.target.value;
    setTimeFrom(nextFrom);
    setTimeToError(getTimeRangeError(nextFrom, timeTo));
  };

  const handleTimeToChange = e => {
    const nextTo = e.target.value;
    setTimeTo(nextTo);
    setTimeToError(getTimeRangeError(timeFrom, nextTo));
  };

  const handleSave = () => {
    if (!status) return;

    const nextTimeToError = showHours ? getTimeRangeError(timeFrom, timeTo) : "";
    setTimeToError(nextTimeToError);
    if (nextTimeToError) return;

    onSave({
      eStatus: status,
      time_from: showHours && timeFrom ? timeFrom : null,
      time_to: showHours && timeTo ? timeTo : null,
      vNotes: notes,
      languages: [],
    });
  };

  const handleClear = () => {
    onClear();
  };

  return (
    <PopupModal
      open={open}
      handleClose={onClose}
      title={cell ? "Edit Availability" : "Add Availability"}
      maxWidth="md"
      fullWidth
      width="650px"
    >
      <Box sx={{ p: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Linguist
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {linguistName}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Date
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {formatDisplayDate(date)}
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <FormLabel>Status</FormLabel>
              <Select value={status} onChange={e => setStatus(e.target.value)} displayEmpty>
                <MenuItem value="">Select Status</MenuItem>
                {STATUS_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.short} — {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {showHours && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {hourLabels.section}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TimePickerComponent
                  name="time_from"
                  label={hourLabels.from}
                  value={timeFrom}
                  onChange={handleTimeFromChange}
                />
              </Grid>
              <Grid item xs={6}>
                <TimePickerComponent
                  name="time_to"
                  label={hourLabels.to}
                  value={timeTo}
                  onChange={handleTimeToChange}
                  error={timeToError}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextFieldComponent
              name="notes"
              label="Notes"
              type="text"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              isNotMandatory
              multiline
              minRows={3}
              placeHolder="Add notes..."
            />
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3 }}>
          {cell && (
            <Button color="error" onClick={handleClear} startIcon={<Delete />} disabled={saving}>
              Clear
            </Button>
          )}
          <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
            <Button onClick={onClose} variant="outlined" disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!status || saving}
              startIcon={<Edit />}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Box>
    </PopupModal>
  );
};

export default CellEditorPopover;
